/* Bastant site 2026 — system pages (cart drawer, cart page, search, 404, password, gift card).
   Impact keeps doing the heavy lifting (cart drawer re-render, quantity changes, predictive search);
   this file only adds the small bits the new design needs. Everything is delegated, so it keeps working
   after Impact replaces the drawer markup. */
(() => {
  if (window.__bsSystem) return;
  window.__bsSystem = true;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* Danish money: 1.199 kr. / 99 kr. / 49,50 kr. (same output as snippets/bastant-lp-money). */
  const money = (cents) => {
    const value = Math.max(0, Math.round(cents));
    const kr = Math.floor(value / 100);
    const ore = value % 100;
    const krText = String(kr).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${krText}${ore ? ',' + String(ore).padStart(2, '0') : ''} kr.`;
  };

  /* ---------- Free-shipping meter ---------- */
  class BsFreeShipping extends HTMLElement {
    connectedCallback() {
      this._onChange = (event) => this.update(event.detail && event.detail.cart);
      document.addEventListener('cart:change', this._onChange);
    }
    disconnectedCallback() {
      document.removeEventListener('cart:change', this._onChange);
    }
    update(cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      const rate = (window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate)) || 1;
      const threshold = Math.round(parseFloat(this.getAttribute('threshold')) * rate);
      const items = cart.items.filter((item) => item.requires_shipping).reduce((sum, item) => sum + item.final_line_price, 0);
      const discounts = (cart.cart_level_discount_applications || []).reduce((sum, d) => sum + d.total_allocated_amount, 0);
      const total = items - discounts;
      const left = threshold - total;
      const pct = threshold > 0 ? Math.max(0, Math.min(100, (total / threshold) * 100)) : 100;
      this.setAttribute('total', total);
      this.style.setProperty('--bs-ship-progress', `${pct}%`);
      this.toggleAttribute('data-reached', left <= 0);
      const leftEl = this.querySelector('[data-bs-ship-left]');
      if (leftEl) leftEl.textContent = left > 0 ? money(left) : '';
      const bar = this.querySelector('[role="progressbar"]');
      if (bar) bar.setAttribute('aria-valuenow', Math.min(total, threshold));
    }
  }
  if (!customElements.get('bs-free-shipping')) customElements.define('bs-free-shipping', BsFreeShipping);

  /* ---------- Gift by amount (snippets/bs-gift-threshold, data from the Bastant portal) ----------
     One fieldset per running tier (data-min / data-max in øre, empty max = no upper limit). The active tier is the one
     with min <= total < max (overlap: highest min, ties: first), same rule as the portal uses on the order.
     No tier, one above: "Køb for N kr. mere, og få en gratis gave." A tier: pick one gift (radios; a gift with sides has one per
     side), and if a tier above adds gifts, "Køb for N kr. mere, og du kan også vælge A eller B".
     A gift picked with the pointer folds the list to "Din gave: X"; keyboard users keep it open, since arrow keys change the radio.
     The choice is the cart attribute "Gave" (variant GID) + "Gave valgt" (title); it is cleared when it is not in the active tier.
     Never dispatches cart:change itself (Impact would re-render the drawer), only bs:gift-change between instances. */
  let giftSaving = Promise.resolve();
  let giftClearing = false;
  const saveGift = (variantId, title) => {
    const attributes = { Gave: variantId || '', 'Gave valgt': variantId ? title || '' : '' };
    giftSaving = giftSaving.catch(() => {}).then(async () => {
      const response = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      if (!response.ok) throw new Error('cart/update failed');
      document.dispatchEvent(new CustomEvent('bs:gift-change', { detail: { variantId: variantId || '' } }));
    });
    return giftSaving;
  };

  class BsGiftThreshold extends HTMLElement {
    connectedCallback() {
      this._onCart = (event) => this.update(event.detail && event.detail.cart);
      this._onGift = (event) => this.reflect(event.detail && event.detail.variantId);
      this._onPick = (event) => {
        const radio = event.target.closest('.bs-gift__radio');
        if (radio && radio.checked) this.choose(radio);
      };
      document.addEventListener('cart:change', this._onCart);
      document.addEventListener('bs:gift-change', this._onGift);
      this._onPointer = () => { this._byPointer = true; };
      this.addEventListener('change', this._onPick);
      this.addEventListener('pointerdown', this._onPointer);
      // server-rendered with an old choice that is not in the active tier (below the amount, or a lower tier): clear it
      const chosen = this.getAttribute('chosen');
      if (chosen && !this.offers(chosen)) this.clear();
    }
    disconnectedCallback() {
      document.removeEventListener('cart:change', this._onCart);
      document.removeEventListener('bs:gift-change', this._onGift);
      this.removeEventListener('change', this._onPick);
      this.removeEventListener('pointerdown', this._onPointer);
    }
    get tiers() {
      const rate = (window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate)) || 1;
      return Array.from(this.querySelectorAll('.bs-gift__pick')).map((el) => {
        const max = el.getAttribute('data-max');
        return {
          el,
          min: Math.round(parseFloat(el.getAttribute('data-min')) * rate),
          max: max ? Math.round(parseFloat(max) * rate) : null,
          count: el.querySelectorAll('.bs-gift__radio').length,
          extras: el.getAttribute('data-extras') || '',
        };
      });
    }
    get active() {
      return this.querySelector('.bs-gift__pick[data-active]');
    }
    /** true when the active tier lists this gift */
    offers(variantId) {
      const tier = this.active;
      return Boolean(tier) && Array.from(tier.querySelectorAll('.bs-gift__radio')).some((radio) => radio.value === variantId);
    }
    update(cart) {
      if (!cart || typeof cart.total_price !== 'number') return;
      const total = cart.total_price;
      const tiers = this.tiers;
      let active = null;
      let next = null;
      tiers.forEach((t) => {
        if (!t.count || !(t.min > 0)) return;
        if (total >= t.min) {
          if ((t.max == null || total < t.max) && (!active || t.min > active.min)) active = t;
        } else if (!next || t.min < next.min) next = t;
      });
      tiers.forEach((t) => t.el.toggleAttribute('data-active', t === active));
      const left = next ? next.min - total : 0;
      const under = !active && Boolean(next);
      const more = Boolean(active && next && next.extras);
      this.setAttribute('total', total);
      this.toggleAttribute('data-under', under);
      this.toggleAttribute('data-more', more);
      const leftEl = this.querySelector('[data-bs-gift-left]');
      if (leftEl) leftEl.textContent = under ? money(left) : '';
      const moreEl = this.querySelector('[data-bs-gift-more-left]');
      if (moreEl) moreEl.textContent = more ? money(left) : '';
      const namesEl = this.querySelector('[data-bs-gift-more-names]');
      if (namesEl) namesEl.textContent = more ? next.extras : '';
      const chosen = (cart.attributes && cart.attributes.Gave) || '';
      if (chosen && !this.offers(chosen)) this.clear();
      else this.reflect(chosen);
    }
    reflect(variantId) {
      if (variantId) this.setAttribute('chosen', variantId);
      else this.removeAttribute('chosen');
      this.querySelectorAll('.bs-gift__radio').forEach((radio) => { radio.checked = Boolean(variantId) && radio.value === variantId; });
      this.querySelectorAll('.bs-gift__pick').forEach((tier) => {
        const radio = tier.querySelector('.bs-gift__radio:checked');
        const fold = tier.querySelector('.bs-gift__fold');
        const label = tier.querySelector('[data-bs-gift-label]');
        if (label) label.textContent = radio ? `Din gave: ${radio.getAttribute('data-title')}` : 'Vælg din gratis gave';
        if (!fold) return;
        fold.toggleAttribute('data-chosen', Boolean(radio));
        if (!radio) fold.open = true;
      });
    }
    status(text) {
      const el = this.querySelector('[data-bs-gift-status]');
      if (el) el.textContent = text;
    }
    async choose(radio) {
      this.status('');
      const fold = radio.closest('.bs-gift__fold');
      const byPointer = this._byPointer;
      this._byPointer = false;
      try {
        await saveGift(radio.value, radio.getAttribute('data-title'));
        if (fold && byPointer) fold.open = false;
      } catch (error) {
        radio.checked = false;
        this.status('Valget blev ikke gemt. Prøv igen.');
      }
    }
    async clear() {
      this.reflect('');
      if (giftClearing) return;
      giftClearing = true;
      try { await saveGift('', ''); } catch (error) { /* tried again on the next cart change */ } finally { giftClearing = false; }
    }
  }
  if (!customElements.get('bs-gift-threshold')) customElements.define('bs-gift-threshold', BsGiftThreshold);

  /* The gift is put on the order after payment (Bastant portal), so checkout cannot list it as an item. Going to checkout we write it
     as the line property "Gratis gave" on the first line, which Shopify shows in the checkout and on the order. No choice yet: the
     tier's first gift, the same one the portal falls back to. The cart itself hides this property (snippets/bs-system-line-item). */
  const GIFT_LINE_KEY = 'Gratis gave';
  const giftForCheckout = async (cart) => {
    const tier = document.querySelector('bs-gift-threshold .bs-gift__pick[data-active]');
    if (!tier) return '';
    const radios = Array.from(tier.querySelectorAll('.bs-gift__radio'));
    const chosen = radios.find((radio) => radio.value === (cart.attributes && cart.attributes.Gave));
    if (chosen) return chosen.getAttribute('data-title');
    if (!radios.length) return '';
    await saveGift(radios[0].value, radios[0].getAttribute('data-title'));
    return radios[0].getAttribute('data-title');
  };
  const syncGiftLine = async () => {
    const getCart = () => fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());
    let cart = await getCart();
    const gift = await giftForCheckout(cart);
    for (let attempt = 0; attempt < 10; attempt += 1) {
      const index = cart.items.findIndex((item, i) => {
        const value = (item.properties && item.properties[GIFT_LINE_KEY]) || '';
        return value !== (i === 0 ? gift : '');
      });
      if (index < 0) return;
      const properties = { ...(cart.items[index].properties || {}) };
      /* Shopify keeps a property that is left out, so "no gift" is an empty value, which checkout and cart do not show */
      properties[GIFT_LINE_KEY] = index === 0 ? gift : '';
      const response = await fetch(`${root()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: index + 1, quantity: cart.items[index].quantity, properties }),
      });
      if (!response.ok) return;
      cart = await response.json();
    }
  };
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    const button = event.submitter;
    if (!button || button.name !== 'checkout' || form.__bsGiftSynced) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    button.disabled = true;
    /* Going to checkout must never depend on this: on any error, continue without the line */
    try { await syncGiftLine(); } catch (error) { /* the gift still follows the order */ }
    button.disabled = false;
    form.__bsGiftSynced = true;
    form.requestSubmit(button);
    form.__bsGiftSynced = false;
  }, true);

  /* ---------- AJAX add to cart (same flow as assets/bastant-lp.js) ---------- */
  async function addToCart(items, source) {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));

    const response = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sections.join(',') }),
    });
    const json = await response.json();

    if (!response.ok) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      throw new Error(json.description || json.message || 'Varen kunne ikke lægges i kurven.');
    }

    const settings = (window.themeVariables && window.themeVariables.settings) || {};
    if (settings.cartType === 'page' || settings.pageType === 'cart') {
      window.location.href = `${root()}cart`;
      return;
    }

    const cart = await (await fetch(`${root()}cart.js`)).json();
    cart.sections = json.sections;

    (source || document.documentElement).dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: json.items || [json], cart } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-bs-sys-add]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const id = form.querySelector('[name="id"]');
    if (!id || (button && button.getAttribute('aria-busy') === 'true')) return;
    if (button) { button.setAttribute('aria-busy', 'true'); button.disabled = true; }
    try {
      await addToCart([{ id: Number(id.value), quantity: 1 }], form);
    } catch (error) {
      if (button) {
        const label = button.querySelector('[data-bs-label]') || button;
        const original = label.textContent;
        label.textContent = 'Udsolgt';
        setTimeout(() => { label.textContent = original; }, 2500);
      }
    } finally {
      if (button && button.isConnected) { button.removeAttribute('aria-busy'); button.disabled = false; }
    }
  });

  /* ---------- Search page: filters/sort submit on change ---------- */
  document.addEventListener('change', (event) => {
    const form = event.target.closest('form[data-bs-autosubmit]');
    if (!form || event.target.matches('[data-bs-no-autosubmit]')) return;
    submitClean(form);
  });
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-bs-autosubmit]');
    if (!form) return;
    event.preventDefault();
    submitClean(form);
  });
  function submitClean(form) {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (String(value).trim() !== '') params.append(key, value);
    });
    window.location.href = `${form.getAttribute('action') || window.location.pathname}?${params.toString()}`;
  }
  /* Close other filter dropdowns when one opens */
  document.addEventListener('toggle', (event) => {
    const el = event.target;
    if (!(el instanceof HTMLDetailsElement) || !el.open || !el.matches('[data-bs-drop]')) return;
    document.querySelectorAll('details[data-bs-drop][open]').forEach((other) => { if (other !== el) other.open = false; });
  }, true);
  document.addEventListener('click', (event) => {
    if (event.target.closest('details[data-bs-drop]')) return;
    document.querySelectorAll('details[data-bs-drop][open]').forEach((d) => { d.open = false; });
  });

  /* ---------- Copy button (gift card) ---------- */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-bs-copy]');
    if (!button) return;
    const label = button.querySelector('[data-bs-label]') || button;
    const original = label.textContent;
    try {
      await navigator.clipboard.writeText(button.getAttribute('data-bs-copy'));
      label.textContent = button.getAttribute('data-bs-copied') || 'Kopieret';
    } catch (error) {
      const input = document.querySelector(button.getAttribute('data-bs-copy-fallback') || 'null');
      if (input) { input.select(); document.execCommand('copy'); label.textContent = button.getAttribute('data-bs-copied') || 'Kopieret'; }
    }
    setTimeout(() => { label.textContent = original; }, 2000);
  });

  /* ---------- Dialog openers (password page) ---------- */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-bs-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('data-bs-dialog-open'));
      if (dialog && typeof dialog.showModal === 'function') {
        dialog.showModal();
        const field = dialog.querySelector('input:not([type="hidden"])');
        if (field) field.focus();
      }
      return;
    }
    const closer = event.target.closest('[data-bs-dialog-close]');
    if (closer) closer.closest('dialog')?.close();
  });
  document.querySelectorAll('dialog[data-bs-dialog-autoopen]').forEach((dialog) => {
    if (typeof dialog.showModal === 'function') dialog.showModal();
  });
})();
